/**
 * UsersTrendBreakdownModal — click-to-modal drill-down for the "Users
 * trend (new / returning)" bar chart on the Engagement tab.
 *
 * v1.4 ux-fix redesign: per-user sortable table (mirrors the time-saved
 * drill-down pattern). Columns: User, Kind, First seen, Last seen,
 * Requests, Errors, Avg latency, Cost, Rating.
 *
 * Data flow:
 *   - `useUsersTrendBreakdown(bucket, granularity, filters)` — enabled only
 *     when `bucket !== null`, so the hook is inert while closed.
 */

import * as React from 'react';

import { UserNameCell } from '@/components/UserNameCell';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUsersTrendBreakdown } from '@/hooks/use-engagement';
import type { Filters } from '@/lib/filters-schema';
import {
  formatDateShort,
  formatDateTime,
  formatDateTimeFull,
  formatTimeOnly,
} from '@/lib/format-date';
import { formatBucketLabel, type BucketGranularity } from '@/lib/format-bucket';
import { formatLatency, formatRating, formatTraceCost } from '@/lib/format';
import type { UsersTrendBreakdownUser } from '@/services/api-types';

interface UsersTrendBreakdownModalProps {
  /** ISO timestamp of the clicked bucket — `null` when the modal is closed. */
  bucket: string | null;
  granularity: BucketGranularity;
  filters: Filters;
  onClose: () => void;
}

type SortKey =
  | 'username'
  | 'isRm'
  | 'kind'
  | 'firstSeenAt'
  | 'lastSeenAt'
  | 'requests'
  | 'errors'
  | 'avgLatencyMs'
  | 'totalCost'
  | 'lastRating';

interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

const DEFAULT_SORT: SortState = { key: 'requests', dir: 'desc' };

function formatDate(iso: string): string {
  return formatDateShort(iso);
}

/**
 * "Last seen" within the clicked bucket. Daily/hourly buckets are a single
 * calendar day (already named in the modal title), so the time alone is
 * unambiguous. A weekly bucket spans seven days, so include the date to say
 * which day the last activity fell on — otherwise "22:15" reads ambiguously.
 */
function formatLastSeen(iso: string, granularity: BucketGranularity): string {
  return granularity === 'weekly' ? formatDateTime(iso) : formatTimeOnly(iso);
}



/**
 * Numeric / date comparator. Strings fall back to localeCompare. Nulls sort
 * last regardless of direction (stable reading experience — missing values
 * never drift to the top).
 */
function compareValues(a: unknown, b: unknown, dir: 'asc' | 'desc'): number {
  const mul = dir === 'asc' ? 1 : -1;
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * mul;
  if (a instanceof Date && b instanceof Date) {
    return (a.getTime() - b.getTime()) * mul;
  }
  return String(a).localeCompare(String(b)) * mul;
}

function sortUsers(
  users: UsersTrendBreakdownUser[],
  sort: SortState,
): UsersTrendBreakdownUser[] {
  const getKey = (u: UsersTrendBreakdownUser): unknown => {
    switch (sort.key) {
      case 'firstSeenAt':
      case 'lastSeenAt':
        return new Date(u[sort.key]);
      case 'isRm':
        return u.isRm ? 1 : 0;
      default:
        return u[sort.key];
    }
  };
  const copy = [...users];
  copy.sort((a, b) => {
    const cmp = compareValues(getKey(a), getKey(b), sort.dir);
    if (cmp !== 0) return cmp;
    return a.username.localeCompare(b.username);
  });
  return copy;
}

function SortButton({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}): React.ReactElement {
  const active = sort.key === sortKey;
  const arrow = active ? (sort.dir === 'asc' ? '↑' : '↓') : '';
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`flex w-full items-center gap-1 font-semibold whitespace-nowrap hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm ${
        active ? 'text-foreground' : 'text-muted-foreground'
      } ${align === 'right' ? 'justify-end' : 'justify-start'}`}
      data-testid={`users-trend-breakdown-sort-${sortKey}`}
    >
      <span>{label}</span>
      <span
        aria-hidden="true"
        className="inline-block w-3 text-center tabular-nums text-[11px] text-foreground"
      >
        {arrow}
      </span>
    </button>
  );
}

function KindBadge({ kind }: { kind: 'new' | 'returning' }): React.ReactElement {
  const cls =
    kind === 'new'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : 'bg-sky-500/15 text-sky-700 dark:text-sky-400';
  return (
    <span
      data-testid={`users-trend-breakdown-kind-${kind}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {kind}
    </span>
  );
}

/**
 * First-seen cell — date text with a tooltip showing the full ISO timestamp
 * and a relative "N days ago" hint. For the "returning" kind this is often
 * the most informative column (how long the user has been with us).
 */
function FirstSeenCell({
  iso,
}: {
  iso: string;
}): React.ReactElement {
  const d = new Date(iso);
  const daysAgo = React.useMemo(() => {
    if (Number.isNaN(d.getTime())) return null;
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }, [d]);
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="cursor-default tabular-nums text-muted-foreground">
          {formatDate(iso)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" sideOffset={6}>
        <div className="text-xs">
          {daysAgo && (
            <div className="font-semibold text-foreground">{daysAgo}</div>
          )}
          <div className="text-muted-foreground tabular-nums">
            {formatDateTimeFull(iso)}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function ErrorsCell({
  errors,
  refusals,
}: {
  errors: number;
  refusals: number;
}): React.ReactElement {
  if (errors === 0 && refusals === 0) {
    return <span className="text-muted-foreground/60">0</span>;
  }
  const total = errors + refusals;
  const hint = [
    errors > 0 ? `${errors} error${errors === 1 ? '' : 's'}` : null,
    refusals > 0
      ? `${refusals} safety refusal${refusals === 1 ? '' : 's'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="cursor-default tabular-nums font-semibold text-destructive">
          {total}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" align="end" sideOffset={6}>
        <span className="text-xs">{hint}</span>
      </TooltipContent>
    </Tooltip>
  );
}

export function UsersTrendBreakdownModal({
  bucket,
  granularity,
  filters,
  onClose,
}: UsersTrendBreakdownModalProps): React.ReactElement {
  const open = bucket !== null;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  const title = bucket === null ? '' : formatBucketLabel(bucket, granularity);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="users-trend-breakdown-modal"
        data-bucket={bucket ?? ''}
        className="max-w-[min(96vw,72rem)] w-[min(96vw,72rem)] max-h-[88vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle data-testid="users-trend-breakdown-title">
            Users · {title}
          </DialogTitle>
          <DialogDescription
            data-testid="users-trend-breakdown-subtitle"
            className="text-xs text-muted-foreground"
          >
            Who was active in this bucket. Click a column header to sort; click
            a username for their full drill-down.
          </DialogDescription>
        </DialogHeader>

        {bucket !== null && (
          <UsersTrendBreakdownBody
            bucket={bucket}
            granularity={granularity}
            filters={filters}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function UsersTrendBreakdownBody({
  bucket,
  granularity,
  filters,
}: {
  bucket: string;
  granularity: BucketGranularity;
  filters: Filters;
}): React.ReactElement {
  const breakdown = useUsersTrendBreakdown(bucket, granularity, filters);
  const [sort, setSort] = React.useState<SortState>(DEFAULT_SORT);

  React.useEffect(() => {
    setSort(DEFAULT_SORT);
  }, [bucket]);

  const onSort = React.useCallback((key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : {
            key,
            dir:
              key === 'username' ||
              key === 'kind' ||
              key === 'firstSeenAt' ||
              key === 'lastSeenAt'
                ? 'asc'
                : 'desc',
            // 'isRm' falls through to 'desc' so RM rows surface first.
          },
    );
  }, []);

  const users = breakdown.data?.users ?? [];
  const newCount = breakdown.data?.newCount ?? 0;
  const returningCount = breakdown.data?.returningCount ?? 0;
  const sorted = React.useMemo(() => sortUsers(users, sort), [users, sort]);

  // `/user/:username` deep-links carry the clicked bucket window so the target
  // page opens zoomed into the same time range. The endpoint returns only the
  // bucket start, so derive the end from the start + the granularity span the
  // trend chart bucketed by: hourly +1h, weekly +7d, otherwise +1d. Without
  // this the link dropped `to` and the user page opened at its default range.
  const from = bucket ?? '';
  const to = React.useMemo(() => {
    if (bucket === null) return '';
    const start = new Date(bucket);
    if (Number.isNaN(start.getTime())) return '';
    const end = new Date(start);
    if (granularity === 'hourly') end.setUTCHours(end.getUTCHours() + 1);
    else if (granularity === 'weekly') end.setUTCDate(end.getUTCDate() + 7);
    else end.setUTCDate(end.getUTCDate() + 1);
    return end.toISOString();
  }, [bucket, granularity]);

  return (
    <div className="min-h-0 flex-1 overflow-hidden flex flex-col gap-2">
      {breakdown.isLoading && (
        <div
          data-testid="users-trend-breakdown-loading"
          className="space-y-2 p-1"
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-8 rounded-md border border-border bg-muted/40 animate-pulse"
            />
          ))}
        </div>
      )}

      {breakdown.isError && (
        <div
          data-testid="users-trend-breakdown-error"
          className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-background p-3 text-sm text-destructive"
        >
          <span>Failed to load users for this bucket.</span>
          <button
            type="button"
            onClick={() => void breakdown.refetch()}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {breakdown.data && !breakdown.isLoading && !breakdown.isError && (
        <>
          <div
            data-testid="users-trend-breakdown-summary"
            className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground"
          >
            <span>
              <span
                data-testid="users-trend-breakdown-new-count"
                className="font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums"
              >
                {newCount}
              </span>{' '}
              new
            </span>
            <span>
              <span
                data-testid="users-trend-breakdown-returning-count"
                className="font-semibold text-sky-700 dark:text-sky-400 tabular-nums"
              >
                {returningCount}
              </span>{' '}
              returning
            </span>
            <span>·</span>
            <span>
              <span className="font-semibold text-foreground tabular-nums">
                {users.reduce((acc, u) => acc + u.requests, 0)}
              </span>{' '}
              requests total
            </span>
            <span>·</span>
            <span data-testid="users-trend-breakdown-role-split">
              RM{' '}
              <span className="font-semibold text-foreground tabular-nums">
                {users.filter((u) => u.isRm).length}
              </span>
              {' · '}
              Non-RM{' '}
              <span className="font-semibold text-foreground tabular-nums">
                {users.filter((u) => !u.isRm).length}
              </span>
            </span>
          </div>

          {sorted.length === 0 ? (
            <div
              data-testid="users-trend-breakdown-empty"
              className="rounded-md border border-border bg-muted/30 p-4 text-center text-sm text-muted-foreground"
            >
              No users active in this bucket.
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              <TooltipProvider delayDuration={200}>
                <Table
                  data-testid="users-trend-breakdown-table"
                  className="text-xs table-fixed w-full"
                >
                  <colgroup>
                    <col style={{ width: '196px' }} />
                    <col style={{ width: '72px' }} />
                    <col style={{ width: '88px' }} />
                    <col style={{ width: '112px' }} />
                    <col style={{ width: '84px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '72px' }} />
                    <col style={{ width: '88px' }} />
                    <col style={{ width: '80px' }} />
                    <col style={{ width: '64px' }} />
                  </colgroup>
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="User"
                          sortKey="username"
                          sort={sort}
                          onSort={onSort}
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Role"
                          sortKey="isRm"
                          sort={sort}
                          onSort={onSort}
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Kind"
                          sortKey="kind"
                          sort={sort}
                          onSort={onSort}
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="First seen"
                          sortKey="firstSeenAt"
                          sort={sort}
                          onSort={onSort}
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Last seen"
                          sortKey="lastSeenAt"
                          sort={sort}
                          onSort={onSort}
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Requests"
                          sortKey="requests"
                          sort={sort}
                          onSort={onSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Errors"
                          sortKey="errors"
                          sort={sort}
                          onSort={onSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Avg lat."
                          sortKey="avgLatencyMs"
                          sort={sort}
                          onSort={onSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Cost"
                          sortKey="totalCost"
                          sort={sort}
                          onSort={onSort}
                          align="right"
                        />
                      </TableHead>
                      <TableHead className="h-9 px-2">
                        <SortButton
                          label="Rating"
                          sortKey="lastRating"
                          sort={sort}
                          onSort={onSort}
                          align="right"
                        />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sorted.map((row) => (
                      <TableRow
                        key={row.username}
                        data-testid="users-trend-breakdown-row"
                        data-username={row.username}
                      >
                        <TableCell className="px-2 align-top">
                          <UserNameCell
                            username={row.username}
                            realName={row.realName}
                            from={from}
                            to={to || undefined}
                            dataTestId="users-trend-breakdown-user"
                          />
                        </TableCell>
                        <TableCell className="px-2">
                          <span
                            data-testid={`users-trend-breakdown-role-${row.isRm ? 'rm' : 'non-rm'}`}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                              row.isRm
                                ? 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {row.isRm ? 'RM' : 'Non-RM'}
                          </span>
                        </TableCell>
                        <TableCell className="px-2">
                          <KindBadge kind={row.kind} />
                        </TableCell>
                        <TableCell className="px-2 truncate">
                          <FirstSeenCell iso={row.firstSeenAt} />
                        </TableCell>
                        <TableCell className="px-2 tabular-nums text-muted-foreground whitespace-nowrap">
                          {formatLastSeen(row.lastSeenAt, granularity)}
                        </TableCell>
                        <TableCell className="px-2 text-right tabular-nums font-semibold">
                          {row.requests}
                        </TableCell>
                        <TableCell className="px-2 text-right">
                          <ErrorsCell
                            errors={row.errors}
                            refusals={row.refusals}
                          />
                        </TableCell>
                        <TableCell className="px-2 text-right tabular-nums text-muted-foreground">
                          {formatLatency(row.avgLatencyMs)}
                        </TableCell>
                        <TableCell className="px-2 text-right tabular-nums text-muted-foreground">
                          {formatTraceCost(row.totalCost)}
                        </TableCell>
                        <TableCell className="px-2 text-right tabular-nums">
                          {formatRating(row.lastRating)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </div>
          )}
        </>
      )}
    </div>
  );
}
